import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Newspaper, Link2, Calendar, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';
import { PageHeader } from './MassTimes';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { AddButton, CardControls } from '../components/AdminControls';
import EditDialog from '../components/EditDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import NewsCard from '../components/NewsCard';

const NEWS_FIELDS = [
  { name: 'date', label: 'Date / Label', required: true, placeholder: 'This Sunday' },
  { name: 'title', label: 'Title', required: true },
  {
    name: 'excerpt', label: 'Excerpt', type: 'textarea', required: true, rows: 4,
    help: 'Any URL you paste (e.g. https://example.com) or email address will automatically become a clickable link on the site.',
  },
];

const LINK_FIELDS = [
  { name: 'title', label: 'Title', required: true, placeholder: 'Archdiocese of Vancouver' },
  { name: 'url', label: 'URL', required: true, placeholder: 'https://example.org', type: 'text' },
  { name: 'description', label: 'Description', type: 'textarea', required: true, rows: 3 },
];

const NewsLinks = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  // Helpful Links state
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [editingLink, setEditingLink] = useState(null);
  const [deleteLinkTarget, setDeleteLinkTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.listNews();
      setNews(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const loadLinks = async () => {
    setLinksLoading(true);
    try {
      const { data } = await api.listHelpfulLinks();
      setLinks(data);
    } catch { /* silent */ }
    finally { setLinksLoading(false); }
  };

  useEffect(() => { load(); loadLinks(); }, []);

  const handleSave = async (values) => {
    if (editing?.mode === 'new') {
      const { data } = await api.createNews(values);
      setNews([data, ...news]);
      toast({ title: 'News item added' });
    } else {
      const { data } = await api.updateNews(editing.id, values);
      setNews(news.map(n => n.id === data.id ? data : n));
      toast({ title: 'News item updated' });
    }
  };

  const handleDelete = async () => {
    await api.deleteNews(deleteTarget.id);
    setNews(news.filter(n => n.id !== deleteTarget.id));
    toast({ title: 'News item deleted' });
    setDeleteTarget(null);
  };

  const handlePhotoUpload = async (n, file) => {
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast({ title: 'Please choose a JPEG, PNG, or WebP image', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Image is larger than 10 MB', variant: 'destructive' });
      return;
    }
    setUploadingId(n.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.uploadNewsImage(n.id, fd);
      setNews(prev => prev.map(x => x.id === data.id ? data : x));
      toast({ title: 'Photo uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploadingId(null);
    }
  };

  const handlePhotoRemove = async (n) => {
    setUploadingId(n.id);
    try {
      const { data } = await api.deleteNewsImage(n.id);
      setNews(prev => prev.map(x => x.id === data.id ? data : x));
      toast({ title: 'Photo removed' });
    } catch (e) {
      toast({ title: 'Remove failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploadingId(null);
    }
  };

  const moveNews = async (item, direction) => {
    const idx = news.findIndex(n => n.id === item.id);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= news.length) return;
    const next = [...news];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setNews(next);
    try {
      const { data } = await api.reorderNews(next.map(n => n.id));
      setNews(data);
    } catch (e) {
      toast({ title: 'Reorder failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
      setNews(news);
    }
  };

  const toggleFeatureNews = async (item) => {
    const nextFeatured = !item.featured_on_home;
    setNews(prev => prev.map(n => n.id === item.id ? { ...n, featured_on_home: nextFeatured } : n));
    try {
      const { data } = await api.featureNewsOnHome(item.id, nextFeatured);
      setNews(prev => prev.map(n => n.id === data.id ? data : n));
      toast({ title: nextFeatured ? 'Featured on home page' : 'Removed from home page' });
    } catch (e) {
      setNews(prev => prev.map(n => n.id === item.id ? { ...n, featured_on_home: !nextFeatured } : n));
      toast({ title: 'Update failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    }
  };

  // ---------- Helpful Links handlers ----------
  const handleSaveLink = async (values) => {
    if (editingLink?.mode === 'new') {
      const { data } = await api.createHelpfulLink(values);
      setLinks([...links, data]);
      toast({ title: 'Link added' });
    } else {
      const { data } = await api.updateHelpfulLink(editingLink.id, values);
      setLinks(links.map(l => l.id === data.id ? data : l));
      toast({ title: 'Link updated' });
    }
  };

  const handleDeleteLink = async () => {
    await api.deleteHelpfulLink(deleteLinkTarget.id);
    setLinks(links.filter(l => l.id !== deleteLinkTarget.id));
    toast({ title: 'Link deleted' });
    setDeleteLinkTarget(null);
  };

  const moveLink = async (item, direction) => {
    const idx = links.findIndex(l => l.id === item.id);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= links.length) return;
    const next = [...links];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setLinks(next);
    try {
      const { data } = await api.reorderHelpfulLinks(next.map(l => l.id));
      setLinks(data);
    } catch (e) {
      toast({ title: 'Reorder failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
      setLinks(links);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Stay Connected"
        title="News & Resources"
        subtitle="The latest parish news alongside helpful resources from across the Catholic Church."
      />

      {/* News */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-3">
          <div>
            <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2 flex items-center gap-2">
              <Newspaper size={14} /> Parish News
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-[#2a1a45]">What&rsquo;s Happening</h2>
          </div>
          {isAdmin && <AddButton onClick={() => setEditing({ mode: 'new' })} label="Add News" />}
        </div>

        {loading ? (
          <p className="text-center text-[#78716c] py-8">Loading news…</p>
        ) : news.length === 0 ? (
          <p className="text-center text-[#78716c] py-8">No news items yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {news.map((n, i) => (
              <NewsCard
                key={n.id}
                news={n}
                isAdmin={isAdmin}
                uploading={uploadingId === n.id}
                onEdit={() => setEditing(n)}
                onDelete={() => setDeleteTarget(n)}
                onFileChosen={handlePhotoUpload}
                onRemoveImage={handlePhotoRemove}
                onMoveUp={(item) => moveNews(item, 'up')}
                onMoveDown={(item) => moveNews(item, 'down')}
                onToggleFeature={toggleFeatureNews}
                canMoveUp={i > 0}
                canMoveDown={i < news.length - 1}
              />
            ))}
          </div>
        )}
      </section>

      {/* Helpful Links */}
      <section className="bg-[#f2ead6]/40 py-16" data-testid="helpful-links-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-3">
            <div>
              <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2 flex items-center gap-2">
                <Link2 size={14} /> Resources
              </p>
              <h2 className="font-display text-3xl md:text-4xl text-[#2a1a45]">Helpful Links</h2>
              <p className="text-sm text-[#5a534a] mt-2 max-w-2xl">
                Trusted Catholic resources for prayer, learning, and staying connected with the wider Church.
              </p>
            </div>
            {isAdmin && (
              <AddButton
                onClick={() => setEditingLink({ mode: 'new' })}
                label="Add Link"
              />
            )}
          </div>

          {linksLoading ? (
            <p className="text-center text-[#78716c] py-8">Loading links…</p>
          ) : links.length === 0 ? (
            <p className="text-center text-[#78716c] py-8">No helpful links yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {links.map((link, i) => (
                <div
                  key={link.id}
                  className="relative group bg-white border border-[#e9e0cc] hover:border-[#3a2359] hover:shadow-sm"
                  data-testid={`helpful-link-${link.id}`}
                >
                  {isAdmin && (
                    <CardControls
                      onEdit={() => setEditingLink(link)}
                      onDelete={() => setDeleteLinkTarget(link)}
                    />
                  )}
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-6 flex items-start gap-4"
                  >
                    <div className="w-11 h-11 rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center flex-shrink-0 group-hover:bg-[#3a2359] group-hover:text-[#e6d4a8]">
                      <ExternalLink size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-xl text-[#2a1a45] group-hover:text-[#3a2359] mb-1">{link.title}</h3>
                      <p className="text-sm text-[#5a534a] leading-relaxed">{link.description}</p>
                    </div>
                  </a>

                  {isAdmin && (
                    <div className="px-6 pb-4 -mt-2 flex items-center justify-between gap-2 border-t border-[#f2ead6] pt-3">
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[#a68a5b] font-medium">Reorder</span>
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); moveLink(link, 'up'); }}
                          disabled={i === 0}
                          data-testid={`helpful-link-move-up-${link.id}`}
                          aria-label="Move up"
                          className="inline-flex items-center justify-center w-8 h-8 border border-[#3a2359] text-[#3a2359] bg-white hover:bg-[#3a2359] hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#3a2359] disabled:cursor-not-allowed"
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); moveLink(link, 'down'); }}
                          disabled={i === links.length - 1}
                          data-testid={`helpful-link-move-down-${link.id}`}
                          aria-label="Move down"
                          className="inline-flex items-center justify-center w-8 h-8 border border-[#3a2359] text-[#3a2359] bg-white hover:bg-[#3a2359] hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#3a2359] disabled:cursor-not-allowed"
                        >
                          <ArrowDown size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Parish Calendar CTA */}
          <Link
            to="/parish-life/calendar"
            className="mt-8 flex items-center justify-between gap-4 bg-white border border-[#e9e0cc] hover:border-[#3a2359] p-6 group"
          >
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center group-hover:bg-[#3a2359] group-hover:text-[#e6d4a8]">
                <Calendar size={18} />
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b]">See What's Coming Up</p>
                <h4 className="font-display text-xl text-[#2a1a45] group-hover:text-[#3a2359]">View Parish Calendar</h4>
              </div>
            </div>
            <ChevronRight size={20} className="text-[#3a2359]" />
          </Link>
        </div>
      </section>

      <EditDialog
        open={!!editing}
        title={editing?.mode === 'new' ? 'Add News Item' : 'Edit News Item'}
        fields={NEWS_FIELDS}
        initial={editing?.mode === 'new' ? { date: '', title: '', excerpt: '' } : (editing || {})}
        onClose={() => setEditing(null)}
        onSubmit={handleSave}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this news item?"
        message={deleteTarget ? `“${deleteTarget.title}” will be permanently removed.` : ''}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      <EditDialog
        open={!!editingLink}
        title={editingLink?.mode === 'new' ? 'Add Helpful Link' : 'Edit Helpful Link'}
        fields={LINK_FIELDS}
        initial={editingLink?.mode === 'new' ? { title: '', url: '', description: '' } : (editingLink || {})}
        onClose={() => setEditingLink(null)}
        onSubmit={handleSaveLink}
      />
      <ConfirmDialog
        open={!!deleteLinkTarget}
        title="Delete this link?"
        message={deleteLinkTarget ? `“${deleteLinkTarget.title}” will be permanently removed.` : ''}
        onCancel={() => setDeleteLinkTarget(null)}
        onConfirm={handleDeleteLink}
      />
    </>
  );
};

export default NewsLinks;
